import { isRecord } from "../types/data";

export interface DemoSession {
  version: number;
  demoProfileId: string;
  uid: string;
  expiresAt: number;
  lastActiveAt: number;
  activityPending: boolean;
  sessionId: string;
  demoApiBaseUrl?: string;
}

export const DEMO_SESSION_STORAGE_KEY = "cineparagon_demo_uid";
export const DEMO_LOGOUT_GUARD_KEY = "cineparagon_demo_logout_guard";
export const DEMO_SESSION_STATE_VERSION = 3;
export const DEMO_SESSION_TTL_MS = 14 * 24 * 60 * 60 * 1000;
const DEMO_LOGOUT_GUARD_TTL_MS = 30 * 1000;
export const DEMO_ACTIVITY_EVENT = "cineparagon-demo-activity";
export const DEMO_FRIEND_USER_ID = "demo-template-friend";

export const isDemoUserId = (value: unknown): value is string => typeof value === "string" && /^demo_[A-Za-z0-9_-]{8,}$/.test(value);

const canUseStorage = () => typeof window !== "undefined" && Boolean(window.localStorage);

export const beginDemoLogout = () => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(DEMO_LOGOUT_GUARD_KEY, String(Date.now()));
  } catch {}
};

export const clearDemoLogout = () => {
  if (typeof window === "undefined") return;
  try { window.sessionStorage.removeItem(DEMO_LOGOUT_GUARD_KEY); } catch {}
};

export const isDemoLogoutInProgress = () => {
  if (typeof window === "undefined") return false;
  try {
    const startedAt = Number(window.sessionStorage.getItem(DEMO_LOGOUT_GUARD_KEY));
    if (
      !Number.isFinite(startedAt) ||
      startedAt <= 0 ||
      Date.now() - startedAt > DEMO_LOGOUT_GUARD_TTL_MS
    ) {
      clearDemoLogout();
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

export const clearDemoSession = () => {
  if (!canUseStorage()) return;
  try { window.localStorage.removeItem(DEMO_SESSION_STORAGE_KEY); } catch {}
};

export const readDemoSession = (): DemoSession | null => {
  if (!canUseStorage()) return null;
  try {
    const raw = window.localStorage.getItem(DEMO_SESSION_STORAGE_KEY);
    if (!raw) return null;
    const decoded: unknown = JSON.parse(raw);
    const session = isRecord(decoded) ? decoded : {};
    const demoProfileId = String(session?.demoProfileId || session?.uid || "");
    const expiresAt = Number(session?.expiresAt);
    if (!isDemoUserId(demoProfileId) || !Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
      clearDemoSession();
      return null;
    }
    const lastActiveAt = Number(session?.lastActiveAt);
    const demoApiBaseUrl = String(session?.demoApiBaseUrl || "").replace(/\/$/, "");
    return {
      version: DEMO_SESSION_STATE_VERSION,
      demoProfileId,
      // Keep `uid` while the rest of the app migrates to the explicit local
      // field name used for browser-bound demo restoration.
      uid: demoProfileId,
      expiresAt,
      lastActiveAt: Number.isFinite(lastActiveAt) ? lastActiveAt : Date.now(),
      activityPending: session?.activityPending === true,
      sessionId: String(session.sessionId || ""),
      ...(demoApiBaseUrl ? { demoApiBaseUrl } : {})
    };
  } catch {
    clearDemoSession();
    return null;
  }
};

export const saveDemoSession = ({
  demoProfileId,
  uid,
  expiresAt,
  lastActiveAt = Date.now(),
  activityPending = false,
  sessionId = "",
  demoApiBaseUrl = ""
}: Partial<DemoSession> = {}): DemoSession => {
  const normalizedProfileId = String(demoProfileId || uid || "");
  if (!isDemoUserId(normalizedProfileId)) {
    throw new Error("Некорректный идентификатор демо-сессии");
  }
  const parsedExpiresAt = Number(expiresAt);
  if (!Number.isFinite(parsedExpiresAt) || parsedExpiresAt <= Date.now()) {
    throw new Error("Некорректный срок демо-сессии");
  }
  const parsedLastActiveAt = Number(lastActiveAt);
  const normalizedDemoApiBaseUrl = String(demoApiBaseUrl || "").replace(/\/$/, "");
  const value = {
    version: DEMO_SESSION_STATE_VERSION,
    demoProfileId: normalizedProfileId,
    uid: normalizedProfileId,
    expiresAt: parsedExpiresAt,
    lastActiveAt: Number.isFinite(parsedLastActiveAt)
      ? parsedLastActiveAt
      : Date.now(),
    activityPending: activityPending === true,
    sessionId: String(sessionId || ""),
    ...(normalizedDemoApiBaseUrl
      ? { demoApiBaseUrl: normalizedDemoApiBaseUrl }
      : {})
  };
  if (canUseStorage()) {
    try { window.localStorage.setItem(DEMO_SESSION_STORAGE_KEY, JSON.stringify(value)); } catch {}
  }
  return value;
};

export const markDemoActivity = (userId: string | null | undefined, occurredAt = Date.now()) => {
  const session = readDemoSession();
  if (!session || session.uid !== String(userId || "")) return null;
  const lastActiveAt = Math.max(
    Number(session.lastActiveAt) || 0,
    Number(occurredAt) || Date.now()
  );
  const nextSession = saveDemoSession({
    ...session,
    lastActiveAt,
    activityPending: true
  });
  if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
    window.dispatchEvent(new CustomEvent(DEMO_ACTIVITY_EVENT, {
      detail: { userId: session.uid, lastActiveAt }
    }));
  }
  return nextSession;
};
